import { sparklinePath, type WeeklySeries } from "@/lib/homepage-telemetry";
import { Tile, TileLabel } from "@/components/homepage/tile";

const SPARK_WIDTH = 120;
const SPARK_HEIGHT = 32;
const STROKE_WIDTH = 1.25;
// Vertical inset so the stroke at the series min/max isn't clipped by the viewBox.
const SPARK_PAD = 2;

export type StatTileTrend = "up" | "down" | "flat";

const TREND_WORDS: Record<StatTileTrend, string> = {
  up: "trending up",
  down: "trending down",
  flat: "holding steady",
};

export interface StatTileProps {
  label: string;
  value: string;
  unit?: string;
  series: WeeklySeries;
  /** Marks the tile as an estimate rather than a directly measured value (asterisk on the label). */
  estimated?: boolean;
  trend: StatTileTrend;
}

export function StatTile({ label, value, unit, series, estimated, trend }: StatTileProps) {
  const values = series.map((point) => point.value);
  const linePath = sparklinePath(values, SPARK_WIDTH, SPARK_HEIGHT - 2 * SPARK_PAD);
  const areaPath = linePath
    ? `${linePath} L ${SPARK_WIDTH} ${SPARK_HEIGHT - SPARK_PAD} L 0 ${SPARK_HEIGHT - SPARK_PAD} Z`
    : "";
  const trendWord = TREND_WORDS[trend];
  const min = Math.min(...values);
  const max = Math.max(...values);
  const rangeLabel = `7-day range ${min.toFixed(0)}–${max.toFixed(0)}${unit ? ` ${unit}` : ""}`;
  const ariaLabel = `${label}${estimated ? ", estimated" : ""}: ${value}${unit ? ` ${unit}` : ""}, ${trendWord}, ${rangeLabel}`;
  const stepX = series.length > 1 ? 100 / (series.length - 1) : 0;

  return (
    <Tile className="gap-2">
      <TileLabel>
        {label}
        {estimated && <span aria-hidden="true">*</span>}
      </TileLabel>
      <p
        className="text-2xl font-bold text-primary"
        style={{ textShadow: "var(--text-shadow-md)" }}
      >
        {value}
        {unit && (
          <span className="text-sm text-muted-foreground font-normal ml-1">{unit}</span>
        )}
      </p>
      <svg
        role="img"
        aria-label={ariaLabel}
        viewBox={`0 0 ${SPARK_WIDTH} ${SPARK_HEIGHT}`}
        className="w-full flex-1 min-h-6 text-primary"
        preserveAspectRatio="none"
      >
        <g transform={`translate(0 ${SPARK_PAD})`}>
          {areaPath && (
            <path d={areaPath} fill="oklch(from var(--primary) l c h / 0.12)" stroke="none" />
          )}
          <path
            d={linePath}
            fill="none"
            stroke="currentColor"
            strokeWidth={STROKE_WIDTH}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </g>
      </svg>
      <div className="relative h-3" aria-hidden="true">
        {series.map((point, i) =>
          point.dayLabel ? (
            <span
              key={point.timestamp}
              className="absolute -translate-x-1/2 text-muted-foreground text-caption font-mono"
              style={{ left: `${i * stepX}%` }}
            >
              {point.dayLabel}
            </span>
          ) : null,
        )}
      </div>
    </Tile>
  );
}
